import { registerSwitchGuard } from '../utils/registerSession';
import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import {
  Alert, Box, Button, Chip, Container, Divider, FormControl, FormHelperText,
  Grid, InputLabel, LinearProgress, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { CloudUpload, Description, PlayArrow, Pause, CheckCircleOutline, ArrowForwardRounded } from '@mui/icons-material';
import Papa from 'papaparse';
import axios from 'axios';
import { toast } from 'react-toastify';
import { UserContext } from '../UserContext';
import { DB_FIELD_LABELS, REQUIRED_DB_FIELDS, getImportFields } from '../utils/importFields';
import { IMPORT_ROW_NUMBER } from '../utils/importValues';
import { buildImportReview, suggestImportMapping } from '../utils/importReview';
import { loadImportCheckpoint, runReviewedImport } from '../utils/importRunner';
import {
  defaultImportOptions, importScope, readImportProfiles, saveImportProfile, removeImportProfile,
  readImportDraft, saveImportDraft, fingerprintImport, filterImportMapping, makeImportReport, downloadImportCsv,
} from '../utils/importProfiles';
import { mutationPayloadFingerprint } from '../utils/equipmentConcurrency';
import ImportReviewPanel from './ImportReviewPanel';

const isEmptyRow = row => Object.values(row || {}).every(value => String(value ?? '').trim() === '');
const emptyCounts = { imported: 0, skipped: 0, review: 0, failed: 0, processed: 0, total: 0 };

const sectionStyle = {
  p: { xs: 2.5, md: 3.5 }, borderRadius: 3, borderColor: 'divider',
  scrollMarginTop: 100, minWidth: 0,
};
const wrappingSelectStyle = {
  '&& .MuiSelect-select.MuiSelect-outlined': { whiteSpace: 'normal', overflowWrap: 'anywhere', textOverflow: 'clip', height: 'auto', minHeight: '1.4375em', pr: '40px !important' },
};
const wrappingMenu = { PaperProps: { sx: {
  maxWidth: 'calc(100vw - 32px)',
  '& .MuiMenuItem-root': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 1 },
} } };
function SectionTitle({ number, title, description }) {
  return <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
    <Box aria-hidden="true" sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main', fontSize: 14, fontWeight: 800 }}>{number}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ fontWeight: 750, lineHeight: 1.35 }}>{title}</Typography>
      {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 840 }}>{description}</Typography>}
    </Box>
  </Stack>;
}

export default function ImportData() {
  const { user } = useContext(UserContext);
  const sessionToken = useRef(localStorage.getItem('token')).current;
  const scope = useMemo(() => importScope(user, process.env.REACT_APP_API_BASE_URL), [user]);
  const availableDbFields = useMemo(() => getImportFields(user?.role), [user?.role]);
  const templateHref = useMemo(() => `data:text/csv;charset=utf-8,${encodeURIComponent(`${availableDbFields.join(',')}\r\n`)}`, [availableDbFields]);
  const [readingFile, setReadingFile] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [fileKey, setFileKey] = useState('');
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [options, setOptions] = useState(defaultImportOptions);
  const [importing, setImporting] = useState(false);
  const [pauseRequested, setPauseRequested] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState('');
  const [statusTone, setStatusTone] = useState('info');
  const [profiles, setProfiles] = useState(() => readImportProfiles(scope));
  const [profileName, setProfileName] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [storageWarning, setStorageWarning] = useState('');
  const busyRef = useRef(false);
  const readingRef = useRef(false);
  const stopRef = useRef(false);
  const uploadSequence = useRef(0);
  const mounted = useRef(true);
  const snapshotRef = useRef(null);
  const fileKeyRef = useRef('');

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stopRef.current = true; uploadSequence.current += 1; };
  }, []);
  useEffect(() => { setProfiles(readImportProfiles(scope)); }, [scope]);
  useEffect(() => registerSwitchGuard(() => {
    if (readingRef.current || busyRef.current) {
      window.alert('Pause the import and wait for the current record before changing register.');
      return false;
    }
    return !fileName || window.confirm('Leave this import? You can resume by uploading the same file in this browser.');
  }), [fileName]);
  useEffect(() => {
    if (!fileKey || readingFile) return undefined;
    const timer = setTimeout(() => {
      try { saveImportDraft(scope, fileKey, mapping, options); setStorageWarning(''); }
      catch { setStorageWarning('This browser could not save the mapping and review choices. Keep this page open until your import finishes.'); }
    }, 400);
    return () => clearTimeout(timer);
  }, [scope, fileKey, mapping, options, readingFile]);
  useEffect(() => {
    setMapping(previous => filterImportMapping(previous, headers, availableDbFields));
  }, [availableDbFields, headers]);

  const sourceReview = useMemo(() => buildImportReview(rows, mapping, availableDbFields, options), [rows, mapping, availableDbFields, options]);
  const review = useMemo(() => {
    const conflicts = new Map((snapshot?.outcomes || []).filter(outcome => outcome.code === 'OLD_RFID_CONFLICT').map(outcome => [outcome.index, outcome]));
    if (!conflicts.size) return sourceReview;
    const entries = sourceReview.entries.map(entry => {
      const outcome = conflicts.get(entry.index);
      if (!outcome || entry.status !== 'ready' || mutationPayloadFingerprint(entry.values) !== mutationPayloadFingerprint(outcome.values)) return entry;
      return { ...entry, status: 'review', issues: [...entry.issues, {
        field: 'old_rfid', code: 'server_rfid_conflict', message: outcome.reason,
        rawValue: entry.source[mapping.old_rfid], allowedActions: ['replace', 'blank'],
      }] };
    });
    const counts = entries.reduce((totals, entry) => { totals[entry.status] += 1; return totals; }, { total: entries.length, ready: 0, review: 0, skipped: 0 });
    return { ...sourceReview, entries, counts };
  }, [sourceReview, snapshot, mapping.old_rfid]);
  const missingMappings = REQUIRED_DB_FIELDS.filter(field => !mapping[field]);
  const counts = snapshot?.counts || { ...emptyCounts, total: rows.length };
  const terminalIndexes = useMemo(() => new Set((snapshot?.outcomes || []).filter(outcome => outcome.status === 'imported' || (outcome.status === 'skipped' && outcome.terminal !== false)).map(outcome => outcome.index)), [snapshot]);
  const pendingReady = review.entries.filter(entry => entry.status === 'ready' && !terminalIndexes.has(entry.index)).length;
  const recentOutcomes = (snapshot?.outcomes || []).slice(-100);
  const settingsLocked = importing || readingFile || Boolean(snapshot?.pending);

  const updateSnapshot = next => {
    snapshotRef.current = next;
    if (mounted.current) setSnapshot({ ...next, outcomes: [...next.outcomes], counts: { ...next.counts } });
  };
  const changeOptions = next => {
    if (settingsLocked) return;
    setOptions(next); setStatus('');
  };
  const changeMapping = (field, column) => {
    if (settingsLocked) return;
    setMapping(previous => {
      const next = { ...previous };
      if (column) next[field] = column; else delete next[field];
      return next;
    });
    // A previous correction belongs to its source column, not a new mapping.
    setOptions(previous => ({ ...previous, resolutions: {} }));
    setStatus('');
  };
  const handleDownloadExcel = async () => {
    setDownloadingTemplate(true);
    try { const { downloadImportWorkbook } = await import('../utils/importWorkbook'); await downloadImportWorkbook(availableDbFields); }
    catch { toast.error('The Excel template could not be generated. Please try again.'); }
    finally { setDownloadingTemplate(false); }
  };
  const handleUpload = async event => {
    const input = event.target;
    const file = input.files[0];
    if (!file || busyRef.current || readingRef.current) return;
    if (!/\.(csv|xlsx)$/i.test(file.name) || file.size > 20 * 1024 * 1024) {
      toast.error('Choose a CSV or Excel (.xlsx) file smaller than 20 MB.'); input.value = ''; return;
    }
    const sequence = ++uploadSequence.current;
    readingRef.current = true; setReadingFile(true);
    setRows([]); setHeaders([]); setFileName(''); setFileKey(''); fileKeyRef.current = '';
    setMapping({}); setOptions(defaultImportOptions()); setSnapshot(null); snapshotRef.current = null;
    setStatus(''); setSelectedProfile('');
    try {
      let parsed;
      if (/\.xlsx$/i.test(file.name)) {
        const { readImportWorkbook } = await import('../utils/importWorkbook');
        const buffer = await new Promise((resolve, reject) => {
          const reader = new FileReader(); reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('The Excel file could not be read.'));
          reader.readAsArrayBuffer(file);
        });
        parsed = await readImportWorkbook(buffer);
      } else {
        parsed = await new Promise((resolve, reject) => Papa.parse(file, {
          header: true, dynamicTyping: false, skipEmptyLines: true,
          transformHeader: header => header.trim(), complete: resolve, error: reject,
        }));
      }
      if (parsed.errors?.length) throw new Error('The CSV has malformed or inconsistent columns. Correct the file before importing.');
      const sourceHeaders = parsed.meta?.fields || Object.keys(parsed.data?.[0] || {});
      if (!sourceHeaders.length || sourceHeaders.some(header => !header) || new Set(sourceHeaders).size !== sourceHeaders.length || Object.keys(parsed.meta?.renamedHeaders || {}).length) throw new Error('Use nonempty, unique column headers.');
      const sourceRows = (parsed.data || []).map((row, index) => {
        if (!row[IMPORT_ROW_NUMBER]) Object.defineProperty(row, IMPORT_ROW_NUMBER, { value: index + 2 });
        return row;
      }).filter(row => !isEmptyRow(row));
      if (!sourceRows.length) throw new Error('The uploaded file is empty.');
      const key = await fingerprintImport({ headers: sourceHeaders, rows: sourceRows.map(row => ({ rowNumber: row[IMPORT_ROW_NUMBER], data: row })) });
      const draft = readImportDraft(scope, key);
      const restoredMapping = filterImportMapping(draft?.mapping || suggestImportMapping(sourceHeaders, availableDbFields), sourceHeaders, availableDbFields);
      const restoredOptions = { ...defaultImportOptions(), ...(draft?.options || {}) };
      let saved = null;
      try { saved = await loadImportCheckpoint(scope, key); }
      catch { setStorageWarning('Saved progress is unavailable in this browser. Import will only start when progress can be saved.'); }
      if (sequence !== uploadSequence.current || sessionToken !== localStorage.getItem('token')) return;
      setFileName(file.name); setFileKey(key); fileKeyRef.current = key; setHeaders(sourceHeaders); setRows(sourceRows);
      setMapping(restoredMapping); setOptions(restoredOptions);
      if (saved) { updateSnapshot(saved); setStatus('Saved progress restored. Confirmed imports will not be sent again.'); setStatusTone('info'); }
      toast.success(`${sourceRows.length.toLocaleString()} records loaded for review.`);
    } catch (error) {
      if (sequence === uploadSequence.current) { setStatus(error.message); setStatusTone('error'); toast.error(error.message); }
    } finally {
      if (sequence === uploadSequence.current) { readingRef.current = false; setReadingFile(false); }
      input.value = '';
    }
  };
  const handleSaveProfile = () => {
    const name = profileName.trim();
    if (!name) { toast.error('Enter a profile name.'); return; }
    try { setProfiles(saveImportProfile(scope, name, mapping, options)); setSelectedProfile(name); toast.success('Mapping profile saved in this browser.'); }
    catch { toast.error('The profile could not be saved in this browser.'); }
  };
  const handleLoadProfile = name => {
    setSelectedProfile(name);
    if (!profiles[name]) return;
    const profile = profiles[name];
    const filtered = filterImportMapping(profile.mapping, headers, availableDbFields);
    setMapping(filtered); setOptions({ ...defaultImportOptions(), ...profile.options, resolutions: {} }); setProfileName(name); setStatus('');
    if (Object.keys(filtered).length < Object.keys(profile.mapping).length) toast.warning('Some saved columns are absent or unavailable. Review the mapping.');
  };
  const handleImport = async () => {
    if (busyRef.current || readingRef.current || !fileKey || !rows.length) return;
    if (missingMappings.length) { toast.error('Map Equipment tag before importing.'); return; }
    if (!pendingReady && !snapshot?.pending) { toast.info('There are no new ready records. Resolve the review items first.'); return; }
    if (!sessionToken || sessionToken !== localStorage.getItem('token')) { toast.error('Your session changed. Sign in and reopen the import.'); return; }
    busyRef.current = true; stopRef.current = false; setImporting(true); setPauseRequested(false);
    setStatus('Importing ready records. Records needing review will remain in the exceptions report.'); setStatusTone('info');
    try {
      saveImportDraft(scope, fileKey, mapping, options);
      const result = await runReviewedImport({
        review, runKey: fileKey, scope,
        shouldStop: () => stopRef.current || sessionToken !== localStorage.getItem('token'),
        postItem: payload => axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/equipment/add`, payload, {
          timeout: 30000,
          headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json', 'X-Client': 'web' },
        }),
        onProgress: updateSnapshot,
      });
      updateSnapshot(result);
      const c = result.counts;
      const summary = `${c.imported} imported, ${c.skipped} skipped, ${c.review} need review, ${c.failed} failed`;
      setStatus(`${result.state === 'complete' ? 'Import completed' : 'Import paused'}: ${summary}.${result.reason ? ` ${result.reason}` : ''}`);
      setStatusTone(result.state !== 'complete' || c.review || c.skipped || c.failed ? 'warning' : 'success');
    } catch (error) {
      setStatus(`Import paused: ${error.message || 'Unexpected error'}. Saved progress is retained.`); setStatusTone('error');
    } finally {
      busyRef.current = false;
      if (mounted.current) { setImporting(false); setPauseRequested(false); }
    }
  };
  const handlePause = () => { stopRef.current = true; setPauseRequested(true); setStatus('Pausing after the current record...'); };
  const downloadReport = exceptionsOnly => {
    const report = makeImportReport(review, snapshot, exceptionsOnly);
    if (!report.length) { toast.info('There are no exceptions to export.'); return; }
    downloadImportCsv(report, `${fileName.replace(/\.[^.]+$/, '')}_${exceptionsOnly ? 'exceptions' : 'import_report'}.csv`);
  };

  return <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, pb: 6, minWidth: 0 }}>
    <Stack spacing={3}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.7, fontWeight: 750 }}>Register setup</Typography>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.8, mt: 0.25 }}>Import Data</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 760, lineHeight: 1.7 }}>Bring your equipment records into EXREG. Match the columns, review any issues, and import when you’re ready.</Typography>
      </Box>
      <Box component="nav" aria-label="Import steps" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
        {[
          ['Upload file', 'import-upload'], ['Match columns', 'import-mapping'],
          ['Review records', 'import-review'], ['Import', 'import-submit'],
        ].map(([label, anchor], index) => <Button key={anchor} component="a" href={`#${anchor}`} disabled={!fileName && index > 0} sx={{
          justifyContent: 'flex-start', gap: 1.25, px: { xs: 1.5, sm: 2 }, py: 1.25,
          border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper',
          color: 'text.primary', textTransform: 'none', minWidth: 0, fontWeight: 650,
        }}>
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>{index + 1}</Box>{label}
        </Button>)}
      </Box>
      <Paper id="import-upload" variant="outlined" sx={sectionStyle}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ width: 54, height: 54, flexShrink: 0, borderRadius: 2.5, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', color: 'primary.main' }}><CloudUpload sx={{ fontSize: 28 }} /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 750 }}>{fileName ? 'Your file is ready to review' : 'Start with your file'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Upload the original CSV or Excel file. No copying into a template required.</Typography>
                <Typography variant="caption" color="text.secondary">CSV or XLSX · up to 20 MB</Typography>
              </Box>
            </Stack>
            <Button variant="contained" component="label" disableElevation startIcon={<CloudUpload />} disabled={importing || readingFile} sx={{ flexShrink: 0, py: 1.2, px: 2.5, borderRadius: 2 }}>
              {readingFile ? 'Reading file...' : 'Upload File'}
              <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden disabled={importing || readingFile} onChange={handleUpload} />
            </Button>
          </Stack>
          {fileName && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
            <CheckCircleOutline color="success" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: 'anywhere', minWidth: 0 }}>{fileName}</Typography>
            <Typography variant="body2" color="text.secondary">{rows.length.toLocaleString()} source records</Typography>
          </Stack>}
          <Divider />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: { md: 'auto' } }}>Starting a new register from scratch? Use a blank template.</Typography>
            <Button onClick={handleDownloadExcel} disabled={downloadingTemplate} startIcon={<Description />} sx={{ justifyContent: { xs: 'flex-start', md: 'center' } }}>{downloadingTemplate ? 'Preparing template...' : 'Download Excel Template'}</Button>
            <Button component="a" href={templateHref} download="import_template.csv" sx={{ justifyContent: { xs: 'flex-start', md: 'center' } }}>Download CSV Template</Button>
          </Stack>
        </Stack>
      </Paper>
      {storageWarning && <Alert severity="warning" sx={{ borderRadius: 2 }}>{storageWarning}</Alert>}
      {status && <Alert severity={statusTone} role="status" sx={{ borderRadius: 2 }}>{status}</Alert>}
      {fileName && <>
        <Paper id="import-mapping" variant="outlined" sx={sectionStyle}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-start' }}>
              <SectionTitle number="2" title="Match your columns" description="We’ve suggested matches from your file. Confirm each one below; leave fields unmapped when they don’t apply." />
              <Chip label={`${Object.keys(mapping).length} of ${availableDbFields.length} mapped`} variant="outlined" sx={{ alignSelf: 'flex-start', flexShrink: 0 }} />
            </Stack>
            <Box sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Reuse a client mapping</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap" alignItems={{ md: 'flex-start' }}>
                <FormControl size="small" sx={{ width: { xs: '100%', md: 240 }, minWidth: 0 }} disabled={settingsLocked}>
                  <InputLabel id="saved-import-profile-label">Saved client profile</InputLabel>
                  <Select labelId="saved-import-profile-label" label="Saved client profile" value={selectedProfile} onChange={event => handleLoadProfile(event.target.value)} sx={wrappingSelectStyle} MenuProps={wrappingMenu}>
                    <MenuItem value="">None</MenuItem>{Object.keys(profiles).map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Profile name" value={profileName} onChange={event => setProfileName(event.target.value)} disabled={settingsLocked} sx={{ width: { xs: '100%', md: 240 }, minWidth: 0 }} />
                <Button variant="outlined" onClick={handleSaveProfile} disabled={settingsLocked || !profileName.trim()} sx={{ minHeight: 40 }}>Save mapping profile</Button>
                <Button disabled={settingsLocked || !selectedProfile} onClick={() => {
                  try { setProfiles(removeImportProfile(scope, selectedProfile)); setSelectedProfile(''); } catch { toast.error('Could not remove the profile.'); }
                }} sx={{ minHeight: 40 }}>Remove profile</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Save column matches, date format and inspection conversions for next time. Profiles stay in this browser; individual corrections stay with this file.</Typography>
            </Box>
            {snapshot?.pending && <Alert severity="warning">A request is awaiting confirmation. Resume it with the saved settings before changing the mapping or review choices.</Alert>}
            {snapshot?.outcomes?.some(outcome => outcome.status === 'imported') && <Alert severity="info">Already imported records remain unchanged. Review changes apply only to records that have not been imported.</Alert>}
            <Grid container spacing={2.5}>
              {availableDbFields.map(field => <Grid item xs={12} sm={6} md={4} lg={3} key={field} sx={{ minWidth: 0 }}>
                <FormControl fullWidth size="small" required={REQUIRED_DB_FIELDS.includes(field)} disabled={settingsLocked} error={REQUIRED_DB_FIELDS.includes(field) && !mapping[field]}>
                  <InputLabel id={`${field}-label`}>{DB_FIELD_LABELS[field]}</InputLabel>
                  <Select labelId={`${field}-label`} id={`${field}-select`} label={DB_FIELD_LABELS[field]} value={mapping[field] || ''} onChange={event => changeMapping(field, event.target.value)} sx={wrappingSelectStyle} MenuProps={wrappingMenu}>
                    <MenuItem value=""><em>Not mapped</em></MenuItem>
                    {headers.filter(header => !Object.values(mapping).includes(header) || mapping[field] === header).map(header => <MenuItem key={header} value={header}>{header}</MenuItem>)}
                  </Select>
                  {field === 'equipment_tag' && <FormHelperText>Required. Missing or duplicate tags are skipped.</FormHelperText>}
                  {field === 'old_rfid' && <FormHelperText>Optional hardware UID; does not confirm a physical scan.</FormHelperText>}
                </FormControl>
              </Grid>)}
            </Grid>
            <Button component="a" href="#import-review" endIcon={<ArrowForwardRounded />} sx={{ alignSelf: 'flex-start' }}>Continue to review</Button>
          </Stack>
        </Paper>
        <ImportReviewPanel completedIndexes={new Set((snapshot?.outcomes || []).filter(outcome => outcome.status === 'imported').map(outcome => outcome.index))} review={review} rows={rows} mapping={mapping} headers={headers} options={options} onOptionsChange={changeOptions} disabled={settingsLocked} onDownloadExceptions={() => downloadReport(true)} onDownloadReport={() => downloadReport(false)} />
        <Paper id="import-submit" variant="outlined" sx={{ ...sectionStyle, borderColor: 'primary.main' }}>
          <Stack spacing={2.5}>
            <SectionTitle number="4" title="Import ready records" description="Only ready records will be imported. You can resolve the remaining issues and return to them later." />
            <Box sx={{ p: 2.5, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5, color: 'primary.main' }}>{pendingReady.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">ready records waiting to be imported</Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="contained" disableElevation startIcon={<PlayArrow />} disabled={importing || readingFile || missingMappings.length > 0 || (!pendingReady && !snapshot?.pending)} onClick={handleImport} sx={{ py: 1.15, px: 2.5, borderRadius: 2 }}>{importing ? 'Importing...' : snapshot ? 'Resume ready records' : 'Import ready records'}</Button>
                  <Button variant="outlined" startIcon={<Pause />} disabled={!importing || pauseRequested} onClick={handlePause} sx={{ borderRadius: 2 }}>{pauseRequested ? 'Pausing...' : 'Pause import'}</Button>
                </Stack>
              </Stack>
            </Box>
            <Typography variant="body2" color="text.secondary">Progress is saved in this browser. To continue later, upload the same file in the same register.</Typography>
            {snapshot && <>
              <Typography>{counts.imported} imported, {counts.skipped} skipped, {counts.review} need review, {counts.failed} failed</Typography>
              <LinearProgress variant="determinate" value={counts.total ? Math.min(100, counts.processed / counts.total * 100) : 0} aria-label="Import processing progress" sx={{ height: 7, borderRadius: 1 }} />
              <Typography variant="caption">{counts.processed} / {counts.total} records processed</Typography>
            </>}
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Import activity</Typography>
              <Button startIcon={<Description />} onClick={() => downloadReport(false)} disabled={importing} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>Download import report</Button>
            </Stack>
            <Box role="log" aria-live="off" sx={{ maxHeight: 300, overflowY: 'auto', overflowWrap: 'anywhere' }}>
              {!recentOutcomes.length && <Typography variant="body2" color="text.secondary">Your import activity will appear here.</Typography>}
              {recentOutcomes.map(outcome => <Typography key={outcome.index} variant="body2" sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>Row {outcome.rowNumber} · {outcome.tag || 'No tag'} · {outcome.status}{outcome.reason ? `: ${outcome.reason}` : ''}</Typography>)}
            </Box>
            <Typography variant="caption" color="text.secondary">The full report includes every source row and your review decisions.</Typography>
          </Stack>
        </Paper>
      </>}
    </Stack>
  </Container>;
}
